/**
 * Schedule Manager - Complete UI for Schedule governance type
 * 
 * Manages time-based workflow orchestration and cron scheduling
 * Part of the 15 fundamental workflow types system
 */

import { gql, useMutation, useQuery } from '@ferp/graphql-client';
import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Group,
    JsonInput,
    Menu,
    Modal,
    ScrollArea,
    Select,
    Stack,
    Tabs,
    Text,
    TextInput,
    ThemeIcon,
    Timeline,
    Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconCalendar,
    IconCheck,
    IconClock,
    IconCopy,
    IconDots,
    IconHistory,
    IconPencil,
    IconPlayerPause,
    IconPlayerPlay,
    IconPlaylistAdd,
    IconPlus,
    IconSettings,
    IconTrash,
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Schedule, ScheduleTrigger } from '../types-16-complete';
import { useGenerateId } from '../hooks/useGenerateId';
import { EntityType } from '../types/EntityType';

interface ScheduleManagerProps {
  tenant: string;
  onScheduleChange?: (schedules: Schedule[]) => void;
  readonly?: boolean;
}

export const ScheduleManager: React.FC<ScheduleManagerProps> = ({
  tenant,
  onScheduleChange,
  readonly = false,
}) => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<Partial<Schedule> | null>(null);
  const [loading, setLoading] = useState(false);
  const [nextRuns, setNextRuns] = useState<Record<string, Date[]>>({});
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [formErrors, setFormErrors] = useState<Record<string, string | undefined>>({});

  // FERP 5-layer enforcement: Backend ID generation only
  const { generateId } = useGenerateId();

  // Sample schedules for development
  const sampleSchedules: Schedule[] = useMemo(() => [
    {
      id: 'sched-001',
      name: 'Daily Patient Report Generation',
      cronExpression: '0 6 * * *', // 6 AM daily
      timezone: 'America/New_York',
      triggers: [
        {
          workflow: 'PatientReportWorkflow',
          signal: 'generate_daily_report',
          payload: { report_type: 'daily_summary' },
        },
      ],
      metadata: {
        created_by: 'admin',
        description: 'Generates daily patient summary reports',
        department: 'Healthcare Operations',
      },
    },
    {
      id: 'sched-002',
      name: 'Weekly Compliance Audit',
      cronExpression: '0 9 * * 1', // 9 AM every Monday
      businessCalendar: 'US_HEALTHCARE',
      timezone: 'UTC',
      triggers: [
        {
          workflow: 'ComplianceAuditWorkflow',
          signal: 'start_audit',
          payload: { audit_type: 'weekly', scope: 'full' },
        },
        {
          workflow: 'NotificationWorkflow',
          signal: 'send_audit_notice',
          payload: { recipients: ['compliance@company.com'] },
        },
      ],
      metadata: {
        compliance_required: true,
        priority: 'high',
      },
    },
    {
      id: 'sched-003',
      name: 'Monthly Resource Cleanup',
      cron_expression: '0 2 1 * *', // 2 AM on the 1st of every month
      timezone: 'UTC',
      triggers: [
        {
          workflow: 'ResourceCleanupWorkflow',
          signal: 'cleanup_resources',
          payload: { cleanup_type: 'monthly', retention_days: 90 },
        },
      ],
      metadata: {
        category: 'maintenance',
        estimated_duration: '2 hours',
      },
    },
  ], []);

  // GraphQL wiring
  const GET_SCHEDULES = gql`
    query GetSchedules($tenant: String!) {
      schedules(tenant: $tenant) {
        id
        name
        cronExpression
        businessCalendar
        timezone
        triggers { workflow signal payload }
        metadata
      }
    }
  `;
  const UPSERT_SCHEDULE = gql`
    mutation UpsertSchedule($tenant: String!, $schedule: ScheduleInput!) {
      upsertSchedule(tenant: $tenant, schedule: $schedule) { id }
    }
  `;
  const DELETE_SCHEDULE = gql`
    mutation DeleteSchedule($tenant: String!, $id: ID!) { deleteSchedule(tenant: $tenant, id: $id) }
  `;
  const RUN_SCHEDULE = gql`
    mutation RunSchedule($tenant: String!, $id: ID!) { runSchedule(tenant: $tenant, id: $id) }
  `;

  const { data, error: loadError, loading: loadingSchedules, refetch } = useQuery<Record<string, any>>(GET_SCHEDULES, { variables: { tenant } } as any);
  const [mutateUpsert] = useMutation<Record<string, any>>(UPSERT_SCHEDULE);
  const [mutateDelete] = useMutation<Record<string, any>>(DELETE_SCHEDULE);
  const [mutateRun] = useMutation<Record<string, any>>(RUN_SCHEDULE);

  useEffect(() => {
    const source = data?.schedules || sampleSchedules;
    setSchedules(source as any);
    const nextRunTimes: Record<string, Date[]> = {};
    (source as any[]).forEach((schedule) => {
      nextRunTimes[schedule.id] = calculateNextRuns(schedule.cron_expression, 3);
    });
    setNextRuns(nextRunTimes);
  }, [data, sampleSchedules]);

  const calculateNextRuns = (cronExpression?: string, count: number = 3): Date[] => {
    if (!cronExpression) return [];
    
    // Simplified cron parser for demo - in production use a proper cron library
    const now = new Date();
    const runs: Date[] = [];
    
    for (let i = 1; i <= count; i++) {
      const nextRun = new Date(now);
      nextRun.setDate(now.getDate() + i);
      nextRun.setHours(6, 0, 0, 0); // Simplified - would parse actual cron
      runs.push(nextRun);
    }
    
    return runs;
  };

  const isValidCron = (expr?: string): boolean => {
    if (!expr) return false;
    const parts = expr.trim().split(/\s+/);
    if (parts.length !== 5) return false;
    const fieldRegex = /^[\d\*\/,\-]+$/;
    return parts.every((p) => fieldRegex.test(p));
  };

  const validateSchedule = (s: Partial<Schedule>): string[] => {
    const errors: string[] = [];
    if (!s.name || s.name.trim().length === 0) errors.push('Name is required');
    if (!isValidCron(s.cron_expression)) errors.push('Cron expression is invalid (use 5 fields)');
    if (!s.timezone || s.timezone.trim().length === 0) errors.push('Timezone is required');
    return errors;
  };

  const createNewSchedule = useCallback(() => {
    const newSchedule: Partial<Schedule> = {
      name: '',
      cron_expression: '0 6 * * *', // Default to 6 AM daily
      timezone: 'UTC',
      triggers: [],
      metadata: {},
    };
    setEditingSchedule(newSchedule);
    setSelectedSchedule(null);
    openModal();
  }, [openModal]);

  const editSchedule = useCallback((schedule: Schedule) => {
    setEditingSchedule({ ...schedule });
    setSelectedSchedule(schedule);
    openModal();
  }, [openModal]);

  const saveSchedule = useCallback(async () => {
    if (!editingSchedule || !editingSchedule.name) return;
    
    setLoading(true);
    try {
      const errs = validateSchedule(editingSchedule);
      if (errs.length > 0) {
        const errMap: Record<string, string | undefined> = {};
        if (!editingSchedule.name) errMap['name'] = 'Required';
        if (!isValidCron(editingSchedule.cron_expression)) errMap['cron'] = 'Invalid cron';
        if (!editingSchedule.timezone) errMap['timezone'] = 'Required';
        setFormErrors(errMap);
        notifications.show({ title: 'Validation error', message: errs.join('; '), color: 'red' });
        return;
      }

      // FERP architecture: Backend ID generation (5-layer enforcement)
      const scheduleId = editingSchedule.id || await generateId(EntityType.schedule);

      const scheduleToSave: Schedule = {
        id: scheduleId,
        name: editingSchedule.name,
        cron_expression: editingSchedule.cron_expression,
        business_calendar: editingSchedule.business_calendar,
        timezone: editingSchedule.timezone || 'UTC',
        triggers: editingSchedule.triggers || [],
        metadata: editingSchedule.metadata || {},
      };

      const updatedSchedules = selectedSchedule
        ? schedules.map(s => s.id === selectedSchedule.id ? scheduleToSave : s)
        : [...schedules, scheduleToSave];

      await mutateUpsert({ variables: { tenant, schedule: scheduleToSave } });
      await refetch?.();
      setSchedules(updatedSchedules);
      
      // Update next runs
      const newNextRuns = { ...nextRuns };
      newNextRuns[scheduleToSave.id] = calculateNextRuns(scheduleToSave.cron_expression, 3);
      setNextRuns(newNextRuns);
      
      onScheduleChange?.(updatedSchedules);
      
      notifications.show({
        title: 'Success',
        message: `Schedule "${scheduleToSave.name}" ${selectedSchedule ? 'updated' : 'created'} successfully`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      
      closeModal();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save schedule',
        color: 'red',
        icon: <IconAlertTriangle size={16} />,
      });
    } finally {
      setLoading(false);
    }
  }, [editingSchedule, selectedSchedule, schedules, nextRuns, onScheduleChange, closeModal]);

  const deleteSchedule = useCallback(async (scheduleId: string) => {
    setLoading(true);
    try {
      await mutateDelete({ variables: { tenant, id: scheduleId } });
      await refetch?.();
      const updatedSchedules = schedules.filter(s => s.id !== scheduleId);
      setSchedules(updatedSchedules);
      const newNextRuns = { ...nextRuns };
      delete newNextRuns[scheduleId];
      setNextRuns(newNextRuns);
      onScheduleChange?.(updatedSchedules);
      notifications.show({ title: 'Schedule Deleted', message: 'Schedule has been removed', color: 'orange' });
    } finally {
      setLoading(false);
    }
  }, [schedules, nextRuns, onScheduleChange, mutateDelete, tenant, refetch]);

  const toggleSchedule = useCallback(async (scheduleId: string, enabled: boolean) => {
    const updatedSchedules = schedules.map(s => 
      s.id === scheduleId 
        ? { ...s, metadata: { ...s.metadata, enabled } }
        : s
    );
    setSchedules(updatedSchedules);
    onScheduleChange?.(updatedSchedules);
    
    notifications.show({
      title: enabled ? 'Schedule Enabled' : 'Schedule Disabled',
      message: `Schedule has been ${enabled ? 'activated' : 'paused'}`,
      color: enabled ? 'green' : 'orange',
    });
  }, [schedules, onScheduleChange]);

  const runScheduleNow = useCallback(async (schedule: Schedule) => {
    setLoading(true);
    try {
      await mutateRun({ variables: { tenant, id: schedule.id } });
      
      notifications.show({
        title: 'Schedule Executed',
        message: `"${schedule.name}" has been executed manually`,
        color: 'blue',
        icon: <IconPlayerPlay size={16} />,
      });
    } finally {
      setLoading(false);
    }
  }, [mutateRun, tenant]);

  const getScheduleStatus = (schedule: Schedule) => {
    const isEnabled = schedule.metadata?.enabled !== false;
    const hasUpcomingRuns = (nextRuns[schedule.id] || []).length > 0;
    
    if (!isEnabled) return { color: 'gray', text: 'Disabled' };
    if (hasUpcomingRuns) return { color: 'green', text: 'Active' };
    return { color: 'orange', text: 'Inactive' };
  };

  const formatCronExpression = (cron?: string) => {
    if (!cron) return 'No schedule';
    
    // Basic cron description mapping
    const cronDescriptions: Record<string, string> = {
      '0 6 * * *': 'Daily at 6:00 AM',
      '0 9 * * 1': 'Weekly on Monday at 9:00 AM',
      '0 2 1 * *': 'Monthly on the 1st at 2:00 AM',
      '*/15 * * * *': 'Every 15 minutes',
      '0 */6 * * *': 'Every 6 hours',
    };
    
    return cronDescriptions[cron] || cron;
  };

  return (
    <Stack gap="md">
      <Group justify="apart">
        <div>
          <Title order={3}>
            <Group gap="xs">
              <IconClock size={24} />
              Schedule Management
            </Group>
          </Title>
          <Text size="sm" color="dimmed">
            Time-based workflow orchestration and automation ({schedules.length} schedules)
          </Text>
        </div>
        {!readonly && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={createNewSchedule}
            variant="filled"
          >
            New Schedule
          </Button>
        )}
      </Group>

      {schedules.length === 0 ? (
        <Alert icon={<IconCalendar size={16} />} title="No Schedules Configured">
          <Text size="sm">
            No schedules have been configured yet. Click &quot;New Schedule&quot; to create your first automated workflow trigger.
          </Text>
        </Alert>
      ) : (
        <Stack gap="sm">
          {schedules.map((schedule) => {
            const status = getScheduleStatus(schedule);
            const upcoming = nextRuns[schedule.id] || [];
            
            return (
              <Card key={schedule.id} shadow="sm" padding="md" withBorder>
                <Group justify="apart" ta="left">
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" ta="center">
                      <Text fw={600}>{schedule.name}</Text>
                      <Badge 
                        color={status.color}
                        variant="light"
                        size="sm"
                      >
                        {status.text}
                      </Badge>
                      {schedule.metadata?.priority && (
                        <Badge 
                          color={schedule.metadata.priority === 'high' ? 'red' : 'blue'}
                          variant="outline"
                          size="xs"
                        >
                          {schedule.metadata.priority}
                        </Badge>
                      )}
                    </Group>
                    
                    <Text size="sm" color="dimmed" mt="xs">
                      {formatCronExpression(schedule.cron_expression)} • {schedule.timezone}
                    </Text>
                    
                    <Text size="sm" color="dimmed">
                      {schedule.triggers.length} trigger{schedule.triggers.length !== 1 ? 's' : ''}
                      {schedule.business_calendar && ` • ${schedule.business_calendar} calendar`}
                    </Text>
                    
                    {upcoming.length > 0 && (
                      <Group gap="xs" mt="xs">
                        <Text size="xs" color="dimmed">Next run:</Text>
                        <Text size="xs" fw={500}>
                          {upcoming[0]?.toLocaleString() ?? 'Not scheduled'}
                        </Text>
                      </Group>
                    )}
                  </div>
                  
                  {!readonly && (
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon variant="subtle">
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      
                      <Menu.Dropdown>
                        <Menu.Item 
                          leftSection={<IconPencil size={14} />}
                          onClick={() => editSchedule(schedule)}
                        >
                          Edit Schedule
                        </Menu.Item>
                        <Menu.Item 
                          leftSection={<IconPlayerPlay size={14} />}
                          onClick={() => runScheduleNow(schedule)}
                          disabled={loading}
                        >
                          Run Now
                        </Menu.Item>
                        <Menu.Item 
                          leftSection={schedule.metadata?.enabled !== false ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
                          onClick={() => toggleSchedule(schedule.id, schedule.metadata?.enabled === false)}
                        >
                          {schedule.metadata?.enabled !== false ? 'Disable' : 'Enable'}
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconCopy size={14} />}
                          onClick={async () => {
                            // FERP architecture: Backend ID generation (5-layer enforcement)
                            const duplicateId = await generateId(EntityType.schedule);
                            const duplicate = {
                              ...schedule,
                              id: duplicateId,
                              name: `${schedule.name} (Copy)`
                            };
                            setSchedules([...schedules, duplicate]);
                          }}
                        >
                          Duplicate
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item 
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => deleteSchedule(schedule.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
                
                {upcoming.length > 1 && (
                  <Timeline mt="md" active={-1} lineWidth={1}>
                    {upcoming.slice(0, 3).map((run, index) => (
                      <Timeline.Item 
                        key={index}
                        bullet={
                          <ThemeIcon size={16} radius="xl" color="blue" variant="light">
                            <IconClock size={10} />
                          </ThemeIcon>
                        }
                        title={<Text size="xs">Run {index + 1}</Text>}
                      >
                        <Text size="xs" color="dimmed">
                          {run.toLocaleString()}
                        </Text>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                )}
              </Card>
            );
          })}
        </Stack>
      )}

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={selectedSchedule ? 'Edit Schedule' : 'Create New Schedule'}
        size="lg"
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        {editingSchedule && (
          <Tabs defaultValue="basic" variant="outline">
            <Tabs.List>
              <Tabs.Tab value="basic" leftSection={<IconSettings size={16} />}>
                Basic Info
              </Tabs.Tab>
              <Tabs.Tab value="triggers" leftSection={<IconPlaylistAdd size={16} />}>
                Triggers ({editingSchedule.triggers?.length || 0})
              </Tabs.Tab>
              <Tabs.Tab value="calendar" leftSection={<IconCalendar size={16} />}>
                Calendar & Time
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="basic" pt="md">
              <Stack gap="md">
                <TextInput
                  label="Schedule Name"
                  placeholder="Enter schedule name"
                  required
                  value={editingSchedule.name || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingSchedule({ ...editingSchedule, name: e.target.value })}
                  error={formErrors['name']}
                />
                
                <TextInput
                  label="Cron Expression"
                  placeholder="0 6 * * * (daily at 6 AM)"
                  required
                  value={editingSchedule.cron_expression || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingSchedule({ ...editingSchedule, cron_expression: e.target.value })}
                  description="Unix cron format: minute hour day month weekday"
                  error={formErrors['cron']}
                />
                
                <Group grow>
                  <Select
                    label="Timezone"
                    placeholder="Select timezone"
                    value={editingSchedule.timezone}
                    onChange={(value: string | null) => setEditingSchedule({ ...editingSchedule, timezone: value || 'UTC' })}
                    data={[
                      { value: 'UTC', label: 'UTC' },
                      { value: 'America/New_York', label: 'Eastern Time' },
                      { value: 'America/Chicago', label: 'Central Time' },
                      { value: 'America/Denver', label: 'Mountain Time' },
                      { value: 'America/Los_Angeles', label: 'Pacific Time' },
                    ]}
                    error={formErrors['timezone']}
                  />
                  
                  <TextInput
                    label="Business Calendar"
                    placeholder="Optional calendar name"
                    value={editingSchedule.business_calendar || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingSchedule({ ...editingSchedule, business_calendar: e.target.value })}
                  />
                </Group>
                
                <JsonInput
                  label="Metadata"
                  placeholder="Enter metadata JSON"
                  value={JSON.stringify(editingSchedule.metadata || {}, null, 2)}
                  onChange={(value) => {
                    try {
                      setEditingSchedule({ ...editingSchedule, metadata: JSON.parse(value) });
                    } catch (_e) {
                      // Invalid JSON, ignore
                    }
                  }}
                  minRows={3}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="triggers" pt="md">
              <ScrollArea h={300}>
                <Stack gap="sm">
                  {editingSchedule.triggers?.map((trigger, index) => (
                    <Card key={index} withBorder padding="sm">
                      <Group justify="apart" ta="left">
                        <div style={{ flex: 1 }}>
                          <Group>
                            <TextInput
                              label="Workflow"
                              placeholder="WorkflowName"
                              value={trigger.workflow}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newTriggers = [...(editingSchedule.triggers || [])];
                                newTriggers[index] = { ...trigger, workflow: e.target.value };
                                setEditingSchedule({ ...editingSchedule, triggers: newTriggers });
                              }}
                              size="xs"
                              style={{ flex: 1 }}
                            />
                            
                            <TextInput
                              label="Signal"
                              placeholder="signal_name"
                              value={trigger.signal}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newTriggers = [...(editingSchedule.triggers || [])];
                                newTriggers[index] = { ...trigger, signal: e.target.value };
                                setEditingSchedule({ ...editingSchedule, triggers: newTriggers });
                              }}
                              size="xs"
                              style={{ flex: 1 }}
                            />
                          </Group>
                          
                          <JsonInput
                            label="Payload"
                            placeholder="{}"
                            value={JSON.stringify(trigger.payload || {}, null, 2)}
                            onChange={(value) => {
                              try {
                                const newTriggers = [...(editingSchedule.triggers || [])];
                                newTriggers[index] = { ...trigger, payload: JSON.parse(value) };
                                setEditingSchedule({ ...editingSchedule, triggers: newTriggers });
                              } catch (_e) {
                                // Invalid JSON, ignore
                              }
                            }}
                            size="xs"
                            minRows={2}
                            mt="xs"
                          />
                        </div>
                        
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => {
                            const newTriggers = editingSchedule.triggers?.filter((_: any, i) => i !== index) || [];
                            setEditingSchedule({ ...editingSchedule, triggers: newTriggers });
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  ))}
                  
                  <Button
                    variant="light"
                    leftSection={<IconPlus size={16} />}
                    onClick={() => {
                      const newTrigger: ScheduleTrigger = {
                        workflow: '',
                        signal: '',
                        payload: {},
                      };
                      setEditingSchedule({
                        ...editingSchedule,
                        triggers: [...(editingSchedule.triggers || []), newTrigger],
                      });
                    }}
                  >
                    Add Trigger
                  </Button>
                </Stack>
              </ScrollArea>
            </Tabs.Panel>

            <Tabs.Panel value="calendar" pt="md">
              <Stack gap="md">
                <Alert icon={<IconHistory size={16} />} title="Next Run Preview">
                  <Text size="sm">
                    {editingSchedule.cron_expression 
                      ? `Next runs: ${calculateNextRuns(editingSchedule.cron_expression, 3)
                          .map(d => d.toLocaleString()).join(', ')}`
                      : 'Enter a cron expression to see next run times'
                    }
                  </Text>
                </Alert>
                
                <Text size="sm" fw={500}>Common Cron Patterns:</Text>
                <Stack gap="xs">
                  {[
                    { pattern: '0 6 * * *', desc: 'Daily at 6:00 AM' },
                    { pattern: '0 9 * * 1', desc: 'Weekly on Monday at 9:00 AM' },
                    { pattern: '0 2 1 * *', desc: 'Monthly on the 1st at 2:00 AM' },
                    { pattern: '*/15 * * * *', desc: 'Every 15 minutes' },
                    { pattern: '0 */6 * * *', desc: 'Every 6 hours' },
                  ].map(({ pattern, desc }) => (
                    <Group key={pattern} gap="xs">
                      <Button
                        variant="light"
                        size="xs"
                        onClick={() => setEditingSchedule({ ...editingSchedule, cron_expression: pattern })}
                      >
                        Use
                      </Button>
                      <Text size="xs" style={{ fontFamily: 'monospace' }}>{pattern}</Text>
                      <Text size="xs" color="dimmed">- {desc}</Text>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
        
        <Group justify="apart" mt="md">
          <Button variant="light" onClick={closeModal}>
            Cancel
          </Button>
          
          <Button
            onClick={saveSchedule}
            loading={loading}
            leftSection={<IconCheck size={16} />}
          >
            {selectedSchedule ? 'Update' : 'Create'} Schedule
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
};

export default ScheduleManager;